import React, { useContext, useEffect } from "react";

import LocaleContext from "@/i18n";
import { RegistrationFormInteractionOutro } from "@/service/Model";
import TrackingService from "@/service/Tracking";

import SovendusDefaultGraphic from "./SovendusDefaultGraphic";
import * as Styled from "./styles";

interface Props {
  outro: RegistrationFormInteractionOutro;
}

const httpsUrl = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).protocol === "https:" ? url : undefined;
  } catch {
    return undefined;
  }
};

const PostRegistrationPromo: React.FC<Props> = ({
  outro,
}: Props): React.JSX.Element | null => {
  const promo = outro.promo;
  const code = promo?.trackingCode || outro.campaignId || "";
  const { translation } = useContext(LocaleContext);
  const linkUrl = httpsUrl(promo?.linkUrl);
  const show = !!promo?.enabled && !!linkUrl;

  useEffect(() => {
    if (show) {
      TrackingService.getInstance().trackOnPromoImpression(code);
    }
  }, []);

  if (!show) {
    return null;
  }

  const handleClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    TrackingService.getInstance().trackOnPromoClick(code);
    window.open(linkUrl!, "_blank", "noopener,noreferrer");
  };

  // The localized Sovendus title/body are an all-or-nothing default: they apply only when the
  // editor left every text field empty (whitespace counts as empty). Any custom text (e.g. a bare
  // "Weitere Gewinnspiele entdecken" button) renders exactly what was entered — no voucher copy is
  // forced into the card.
  const customTitle = promo!.title?.trim() || undefined;
  const customBody = promo!.body?.trim() || undefined;
  const customButtonLabel = promo!.buttonLabel?.trim() || undefined;
  const useDefaults = !customTitle && !customBody && !customButtonLabel;
  const title = useDefaults ? translation.promo.defaultTitle : customTitle;
  const body = useDefaults ? translation.promo.defaultBody : customBody;
  const buttonLabel = customButtonLabel ?? translation.promo.defaultButtonLabel;

  return (
    <Styled.Card data-qa="registration.promo">
      <Styled.ImageBox>
        {promo!.image ? (
          <img src={promo!.image} alt={title ?? ""} />
        ) : (
          <SovendusDefaultGraphic />
        )}
      </Styled.ImageBox>
      {title && <Styled.Title>{title}</Styled.Title>}
      {body && <Styled.Body>{body}</Styled.Body>}
      <Styled.Button
        as="a"
        href={linkUrl!}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        data-qa="registration.promo.button"
      >
        {buttonLabel}
      </Styled.Button>
    </Styled.Card>
  );
};

export default PostRegistrationPromo;
